import PropTypes from 'prop-types';
import React from 'react';
import iconSrc from '../../../utils/iconSrc';
import useToggle from '../../../utils/useToggle';
import { PrimaryNavItem } from '../buttons';
import { BlankLink, Item, ItemLink, SubMenuUl } from './styles';
import { ArrowSVG, SubNavButton } from './styles-icons';
import { consistentString } from '../../../utils/content-formatting';

const detectActiveNav = (navLink) => navLink.navigation?.active;

const SubMenuUlComponent = ({
  overlayMenuOpen,
  legal,
  setActiveSubMenu,
  navLevel,
  navLink,
  childNavigationItems,
}) => {
  const [subMenuState, setSubMenuState] = useToggle();

  /* Child navigation links - expando on mobile, positioned floater on desktop */
  return (
    <SubMenuUl
      id="subMenuUl"
      $active={overlayMenuOpen}
      aria-hidden={!overlayMenuOpen}
      legal={legal}
      onBlur={(event) => {
        const nextTargetItem =
          event.relatedTarget?.parentElement.parentElement.id === 'subMenuUl';

        if (!nextTargetItem) {
          setActiveSubMenu(false);
        }
      }}
    >
      {childNavigationItems.map(
        (item, i) =>
          item.navigationLink &&
          (item.childNavigationItems && navLevel < 3 ? (
            <NavigationMenuItem
              key={i}
              id={item.id}
              menuItem={item}
              overlayMenuState={subMenuState}
              setActiveSubMenu={() => setSubMenuState()}
              navLevel={navLevel + 1}
            />
          ) : (
            <Item key={i}>
              <ItemLink
                link={item.navigationLink[0]}
                $activeNavItem={detectActiveNav(item.navigationLink[0])}
                tabindex="-1"
                data={{
                  headerlink: `${consistentString(
                    `${navLink.title}_${item.menuLabel}`
                  )}`,
                }}
              >
                <span>{item.menuLabel}</span>
              </ItemLink>
            </Item>
          ))
      )}
    </SubMenuUl>
  );
};

SubMenuUlComponent.propTypes = {
  overlayMenuOpen: PropTypes.bool,
  legal: PropTypes.bool,
  setActiveSubMenu: PropTypes.func,
  navLevel: PropTypes.number,
  navLink: PropTypes.shape({
    title: PropTypes.string,
    slug: PropTypes.string,
  }),
  childNavigationItems: PropTypes.array,
};

const NavigationMenuItem = ({
  menuItem,
  id,
  overlayMenuOpen,
  setOverlayMenuOpen,
  setActiveSubMenu,
  legal,
  navLevel,
  isDesktop,
  primaryNav = false,
}) => {
  const { menuLabel, navigationLink, childNavigationItems } = menuItem;
  const navLink = navigationLink
    ? navigationLink[0]
    : { title: menuLabel, slug: '#' };

  if (!navigationLink && !childNavigationItems) return null;

  return (
    <Item $topLevel={navLevel === 1} $primaryNav={primaryNav}>
      {navLink.slug !== '#' &&
        // primary desktop navigation link
        (primaryNav && isDesktop ? (
          <>
            <PrimaryNavItem>
              <ItemLink link={navLink} data={{ headerlink: navLink.slug }}>
                {menuLabel || navLink.title}
              </ItemLink>
              <SubNavButton // primary desktop dropdown icon
                type="button"
                onClick={() => {
                  setActiveSubMenu(id);
                  setOverlayMenuOpen(!overlayMenuOpen);
                }}
                className="tracking-subnav-button"
                data-tracking={menuLabel || navLink.title}
                open={overlayMenuOpen}
                aria-haspopup={!!childNavigationItems}
                aria-expanded={overlayMenuOpen}
                aria-label={`${menuLabel || navLink.title} submenu`}
                legal={legal}
                data-headernav="chevron"
                data-headerbutton={`${navLink.slug}_chevron`}
              >
                <ArrowSVG src={iconSrc('chevron-down')} cacheRequests />
              </SubNavButton>
            </PrimaryNavItem>
            {childNavigationItems ? (
              <SubMenuUlComponent
                overlayMenuOpen={overlayMenuOpen}
                navLevel={navLevel}
                navLink={navLink}
                legal={legal}
                childNavigationItems={childNavigationItems}
                setActiveSubMenu={setActiveSubMenu}
              />
            ) : null}
          </>
        ) : (
          // regular navigation links
          <ItemLink
            $activeNavItem={detectActiveNav(navLink)}
            link={navLink}
            data={{ headerlink: navLink.slug }}
          >
            <span
              data-testid={
                detectActiveNav(navLink) === true ? 'active-nav-item' : null
              }
            >
              {menuLabel || navLink.title}
            </span>
          </ItemLink>
        ))}

      {/* Child navigation access - 'topics' button on mobile, named additional links item on desktop */}
      {childNavigationItems && navLevel < 3 && (
        <>
          {navLink.slug === '#' && (
            // empty link access for submenu (eg. scotland more)
            <BlankLink onClick={() => setActiveSubMenu(id)}>
              {menuLabel || navLink.title}
            </BlankLink>
          )}
          {!isDesktop && (
            <>
              <SubNavButton // mobileDrawer dropdown icon
                type="button"
                onClick={() => setActiveSubMenu(id)}
                className="tracking-subnav-button"
                data-tracking={menuLabel || navLink.title}
                open={overlayMenuOpen}
                aria-expanded={overlayMenuOpen}
                aria-label="Housing advice submenu"
                legal={legal}
                data-headernav="chevron"
              >
                {primaryNav && <span>Topics</span>}
                <ArrowSVG
                  src={iconSrc('chevron-down')}
                  $primaryNav={primaryNav}
                  cacheRequests
                />
              </SubNavButton>
              {childNavigationItems ? (
                <SubMenuUlComponent
                  overlayMenuOpen={overlayMenuOpen}
                  navLevel={navLevel}
                  navLink={navLink}
                  legal={legal}
                  childNavigationItems={childNavigationItems}
                  setActiveSubMenu={setActiveSubMenu}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </Item>
  );
};

NavigationMenuItem.propTypes = {
  menuItem: PropTypes.shape({
    navigationLink: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
      })
    ),
    menuLabel: PropTypes.string.isRequired,
    childNavigationItems: PropTypes.array,
  }),
  id: PropTypes.string.isRequired,
  overlayMenuOpen: PropTypes.bool,
  setOverlayMenuOpen: PropTypes.func,
  setActiveSubMenu: PropTypes.func,
  legal: PropTypes.bool,
  navLevel: PropTypes.number,
  primaryNav: PropTypes.bool,
  isDesktop: PropTypes.bool.isRequired,
};

NavigationMenuItem.defaultProps = {
  navLevel: 1,
};

export default NavigationMenuItem;
